import React from 'react';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';

import type { FileResponseItem } from '../models.ts';
import ContextMenu from '@components/ContextMenu/ContextMenu.tsx';
import { ContextMenuItem } from '@components/ContextMenu/ContextMenuItem.tsx';
import { ContextMenuSeparator } from '@components/ContextMenu/ContextMenuSeparator.tsx';
import { ImgTrash } from './Images/ImgTrash.tsx';
import { ImgLink } from './Images/ImgLink.tsx';
import { ImgClock } from './Images/ImgClock.tsx';
import { ImgDownload } from './Images/ImgDownload.tsx';
import { ImgShare } from './Images/ImgShare.tsx';
import { ImgThreeDots } from './Images/ImgThreeDots.tsx';
import { ImgUpload } from './Images/ImgUpload.tsx';
import { getShareUrl } from '@root/utils/fileUtils.ts';
import { toastService } from './Toast.tsx';

export type FileCardMenuProps = {
  file: FileResponseItem;
  onDelete: () => void;
  onPermanentDelete?: () => void;
  onManageExpiration: () => void;
  onCustomize?: () => void;
  onShareCode?: () => void;
  onReupload?: () => void;
  onCustomLink?: () => void;
  /** Pass 'file-actions' for grid cards so it stays visible while menu is open */
  wrapperClassName?: string;
};

@observer
export class FileCardMenu extends React.Component<FileCardMenuProps> {
  private _btnRef = React.createRef<HTMLElement>();

  @observable private _isOpen = false;
  /** Local observable copy of props.file to avoid reading non-observable props in reactive context */
  @observable.ref private _file: FileResponseItem;
  @observable private _wrapperClassName: string | undefined;
  @observable private _onDelete: () => void;
  @observable private _onPermanentDelete: (() => void) | undefined;
  @observable private _onManageExpiration: () => void;
  @observable private _onCustomize: (() => void) | undefined;
  @observable private _onShareCode: (() => void) | undefined;
  @observable private _onReupload: (() => void) | undefined;
  @observable private _onCustomLink: (() => void) | undefined;

  constructor(props: FileCardMenuProps) {
    super(props);
    this._file = props.file;
    this._wrapperClassName = props.wrapperClassName;
    this._onDelete = props.onDelete;
    this._onPermanentDelete = props.onPermanentDelete;
    this._onManageExpiration = props.onManageExpiration;
    this._onCustomize = props.onCustomize;
    this._onShareCode = props.onShareCode;
    this._onReupload = props.onReupload;
    this._onCustomLink = props.onCustomLink;
    makeObservable(this);
  }

  componentDidUpdate(prevProps: FileCardMenuProps) {
    if (prevProps.file !== this.props.file || prevProps.wrapperClassName !== this.props.wrapperClassName) {
      runInAction(() => {
        this._file = this.props.file;
        this._wrapperClassName = this.props.wrapperClassName;
        this._onDelete = this.props.onDelete;
        this._onPermanentDelete = this.props.onPermanentDelete;
        this._onManageExpiration = this.props.onManageExpiration;
        this._onCustomize = this.props.onCustomize;
        this._onShareCode = this.props.onShareCode;
        this._onReupload = this.props.onReupload;
        this._onCustomLink = this.props.onCustomLink;
      });
    }
  }

  @action.bound
  private _onAppear(_elem: HTMLElement): boolean {
    this._isOpen = true;
    return true;
  }

  @action.bound
  private _onDisappear() {
    this._isOpen = false;
  }

  render() {
    const file = this._file;
    const wrapperClassName = this._wrapperClassName;
    const shareLink = file.isFolder && file.isSharedSpace && file.shareToken ? `${window.location.origin}/share-space/${file.shareToken}` : null;
    const btn = (
      <button
        ref={this._btnRef as React.RefObject<HTMLButtonElement | null>}
        className="action-btn file-opts-btn"
        title="Options"
        onClick={(e) => e.stopPropagation()}
      >
        <ImgThreeDots /></button>
    );

    return (
      <>
        {wrapperClassName ? (
          <div
            className={wrapperClassName}
            style={this._isOpen ? { display: 'flex' } : undefined}
            onClick={(e) => e.stopPropagation()}
          >
            {btn}
          </div>
        ) : (
          <div style={{ display: 'inline-flex' }} onClick={(e) => e.stopPropagation()}>
            {btn}
          </div>
        )}
        <ContextMenu container={this._btnRef} showOnClick onAppear={this._onAppear} onDisappear={this._onDisappear}>
          {() => (
            <div onClick={(e) => e.stopPropagation()}>
              {!file.isFolder && (
                <ContextMenuItem title={<><ImgDownload /> Download</>} onClick={() => { window.open(getShareUrl(file), '_blank'); }} />
              )}
              {!file.isFolder && this._onCustomLink && (
                <ContextMenuItem title={<><ImgLink /> Custom Link</>} onClick={this._onCustomLink} />
              )}
              {!file.isFolder && this._onReupload && (
                <ContextMenuItem title={<><ImgUpload /> Re-upload</>} onClick={this._onReupload} />
              )}
              {shareLink && (
                <ContextMenuItem title={<><ImgShare size={16} /> Open Shared Space</>} onClick={() => window.open(`/share-space/${file.shareToken}`, '_blank')} />
              )}
              {shareLink && (
                <ContextMenuItem title={<><ImgLink /> Copy Shared Link</>} onClick={() => this._copyShareLink(shareLink)} />
              )}
              <ContextMenuItem title={<><ImgClock /> Manage Expiration</>} onClick={this._onManageExpiration} />
              {!file.isFolder && this._onCustomize && (
                <ContextMenuItem title={<><ImgShare size={16} /> Customize Share Page</>} onClick={this._onCustomize} />
              )}
              {!file.isFolder && this._onShareCode && (
                <ContextMenuItem title={<><ImgLink /> Share via Code</>} onClick={this._onShareCode} />
              )}
              <ContextMenuSeparator />
              <ContextMenuItem title={<><ImgTrash /> Move to Trash</>} onClick={this._onDelete} />
              {this._onPermanentDelete && (
                <>
                  <ContextMenuSeparator />
                  <ContextMenuItem
                    title={<><ImgTrash style={{ color: 'var(--danger-color)' }} /><span style={{ color: 'var(--danger-color)' }}>Delete Permanently</span></>}
                    onClick={this._onPermanentDelete}
                  />
                </>
              )}
            </div>
          )}
        </ContextMenu>
      </>
    );
  }

  private _copyShareLink = (link: string) => {
    navigator.clipboard.writeText(link)
      .then(() => toastService.info('Shared link copied', 'Share Space'))
      .catch(() => toastService.error('Unable to copy link', 'Share Space'));
  };
}
